import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Mail, Phone, MapPin, Building2, Star,
  Package, Wrench, ShieldCheck, ShieldX, Clock, Calendar,
  ChevronRight, Send, AlertTriangle, FileText, ExternalLink,
  Plus, Check, Edit, Trash2, X, Save, RefreshCw, Unplug, ChevronDown, ChevronUp
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const WarrantyBadge = ({ status, endDate, daysRemaining }) => {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#142915] text-[#00E01A] border border-[#00E01A]/20">
        <ShieldCheck size={12} />
        Sous garantie jusqu'au {new Date(endDate).toLocaleDateString('fr-FR')}
      </span>
    );
  }
  if (status === 'expiring_soon') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#2A2010] text-[#F39C12] border border-[#F39C12]/20">
        <AlertTriangle size={12} />
        Expire dans {daysRemaining} jours
      </span>
    );
  }
  if (status === 'expired') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#2A1515] text-[#E74C3C] border border-[#E74C3C]/20">
        <ShieldX size={12} />
        Garantie expirée
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-[#2C3338] text-[#8C8F94] border border-[#52555A]/20">
      <Clock size={12} />
      Date de livraison requise
    </span>
  );
};

const EquipmentCard = ({ equipment, onEdit, onDelete }) => (
  <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] overflow-hidden">
    <div className="aspect-video bg-[#141617] relative">
      {equipment.product_photos?.[0] ? (
        <img
          src={equipment.product_photos[0]}
          alt={equipment.product_name}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Package size={48} className="text-[#2C3338]" />
        </div>
      )}
      <div className="absolute top-2 left-2">
        <span className="px-2 py-1 rounded text-xs font-medium bg-black/60 backdrop-blur text-white">
          {equipment.product_category === 'esurf' ? 'E-Surf' :
           equipment.product_category === 'efoil' ? 'E-Foil' :
           equipment.product_category === 'boat' ? 'Bateau' : 'Accessoire'}
        </span>
      </div>
      {/* Action buttons */}
      <div className="absolute top-2 right-2 flex gap-1">
        <button
          onClick={() => onEdit(equipment)}
          className="p-1.5 rounded-lg bg-black/60 backdrop-blur text-[#8C8F94] hover:text-[#00E01A] transition-colors"
          title="Modifier"
          data-testid={`edit-equip-${equipment.id}`}
        >
          <Edit size={14} />
        </button>
        <button
          onClick={() => onDelete(equipment)}
          className="p-1.5 rounded-lg bg-black/60 backdrop-blur text-[#8C8F94] hover:text-[#E74C3C] transition-colors"
          title="Supprimer"
          data-testid={`delete-equip-${equipment.id}`}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
    <div className="p-4">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-[#8C8F94] text-xs">{equipment.supplier_name}</p>
          <h4 className="text-white font-semibold">{equipment.product_name}</h4>
          <p className="text-[#8C8F94] text-xs font-mono mt-1">
            N° série: {equipment.serial_number || <span className="text-[#F39C12] italic">À compléter</span>}
          </p>
        </div>
      </div>
      
      <div className="mt-3">
        <WarrantyBadge
          status={equipment.warranty_status}
          endDate={equipment.warranty_end_date}
          daysRemaining={equipment.warranty_days_remaining}
        />
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-[#2C3338]">
        <div>
          <p className="text-[#8C8F94] text-xs">Achat</p>
          <p className="text-white text-sm">
            {equipment.purchase_date 
              ? new Date(equipment.purchase_date).toLocaleDateString('fr-FR')
              : '-'}
          </p>
        </div>
        <div>
          <p className="text-[#8C8F94] text-xs">Livraison</p>
          <p className="text-white text-sm">
            {equipment.delivery_date 
              ? new Date(equipment.delivery_date).toLocaleDateString('fr-FR')
              : <span className="text-[#F39C12] italic text-xs">Non renseignée</span>}
          </p>
        </div>
      </div>

      {equipment.revision_due && (
        <div className="mt-3 p-2 bg-[#2A2010] rounded-lg flex items-center gap-2 text-[#F39C12] text-xs">
          <Clock size={14} />
          <span>Révision recommandée</span>
        </div>
      )}
    </div>
  </div>
);

const TimelineItem = ({ item }) => {
  const icons = {
    purchase: Package,
    delivery: Package,
    revision: Wrench,
    sav: Wrench,
    email: Mail
  };
  const colors = {
    purchase: 'text-[#00E01A] bg-[#142915]',
    delivery: 'text-[#3498DB] bg-[#152A35]',
    revision: 'text-[#F39C12] bg-[#2A2010]',
    sav: 'text-[#E74C3C] bg-[#2A1515]',
    email: 'text-[#8C8F94] bg-[#2C3338]'
  };
  
  const Icon = icons[item.type] || Calendar;
  
  return (
    <div className="flex gap-3">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${colors[item.type]}`}>
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium">{item.title}</p>
        {item.description && (
          <p className="text-[#8C8F94] text-xs mt-0.5 truncate">{item.description}</p>
        )}
        <p className="text-[#52555A] text-xs mt-1">
          {new Date(item.date).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
          })}
        </p>
      </div>
    </div>
  );
};

const RegisterEquipmentDialog = ({ clientId, invoices, api, onCreated, onClose }) => {
  const [invoiceProducts, setInvoiceProducts] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('invoice');
  const [selectedInvoiceProduct, setSelectedInvoiceProduct] = useState(null);
  const [productId, setProductId] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get(`/axonaut/client-products/${clientId}`);
        setInvoiceProducts(res.data.products || []);
        setCatalog(res.data.catalog || []);
      } catch (e) {
        console.error('Failed to fetch client products:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [api, clientId]);

  const handleSelectInvoiceProduct = (product) => {
    setSelectedInvoiceProduct(product);
    if (product.catalog_match) {
      setProductId(product.catalog_match.id);
    } else {
      setProductId('');
    }
    if (product.invoice_date) {
      setPurchaseDate(product.invoice_date.slice(0, 10));
    }
    if (product.total_ttc) {
      setPurchaseAmount(String(product.total_ttc));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productId) {
      toast.error('Veuillez sélectionner un produit du catalogue');
      return;
    }

    setSaving(true);
    try {
      const franchisesRes = await api.get('/franchises');
      const franchiseId = franchisesRes.data[0]?.id;

      await api.post('/equipment', {
        product_id: productId,
        client_id: clientId,
        serial_number: serialNumber.trim() || null,
        purchase_date: purchaseDate || null,
        delivery_date: deliveryDate || null,
        franchise_id: franchiseId,
        status: 'sold',
        axonaut_invoice_ref: selectedInvoiceProduct?.invoice_number || null,
        purchase_amount: purchaseAmount ? parseFloat(purchaseAmount) : null,
      });
      toast.success('Équipement enregistré');
      onCreated();
    } catch (error) {
      console.error('Failed to create equipment:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-[#8C8F94]">Chargement des produits...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      {/* Source selector */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { setSource('invoice'); setProductId(''); setSelectedInvoiceProduct(null); }}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
            source === 'invoice'
              ? 'bg-[#142915] text-[#00E01A] border border-[#00E01A]/30'
              : 'bg-[#141617] text-[#8C8F94] border border-[#2C3338] hover:border-[#52555A]'
          }`}
          data-testid="source-invoice-btn"
        >
          <FileText size={14} className="inline mr-1.5 -mt-0.5" />
          Depuis une facture ({invoiceProducts.length})
        </button>
        <button
          type="button"
          onClick={() => { setSource('catalog'); setSelectedInvoiceProduct(null); setProductId(''); }}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
            source === 'catalog'
              ? 'bg-[#142915] text-[#00E01A] border border-[#00E01A]/30'
              : 'bg-[#141617] text-[#8C8F94] border border-[#2C3338] hover:border-[#52555A]'
          }`}
          data-testid="source-catalog-btn"
        >
          <Package size={14} className="inline mr-1.5 -mt-0.5" />
          Depuis le catalogue ({catalog.length})
        </button>
      </div>

      {/* Invoice products list */}
      {source === 'invoice' && (
        <div className="space-y-2">
          <Label>Sélectionner un produit facturé</Label>
          {invoiceProducts.length === 0 ? (
            <p className="text-[#8C8F94] text-sm py-3 text-center">Aucun produit trouvé dans les factures</p>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="invoice-products-list">
              {invoiceProducts.map((p, i) => {
                const isSelected = selectedInvoiceProduct === p;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleSelectInvoiceProduct(p)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      isSelected
                        ? 'bg-[#142915] border-[#00E01A]/40'
                        : 'bg-[#141617] border-[#2C3338] hover:border-[#52555A]'
                    }`}
                    data-testid={`invoice-product-${i}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-[#00E01A]' : 'text-white'}`}>
                          {p.invoice_product_name}
                        </p>
                        <p className="text-[#8C8F94] text-xs mt-0.5">
                          {p.invoice_number} — {p.invoice_date ? new Date(p.invoice_date).toLocaleDateString('fr-FR') : ''}
                          {p.quantity > 1 && ` — ${p.quantity} unité(s)`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                        {p.catalog_match ? (
                          <span className="text-[#00E01A] text-xs flex items-center gap-0.5">
                            <Check size={12} /> Catalogue
                          </span>
                        ) : (
                          <span className="text-[#52555A] text-xs">Hors catalogue</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* If invoice product selected but no catalog match, show catalog selector */}
          {selectedInvoiceProduct && !selectedInvoiceProduct.catalog_match && (
            <div className="bg-[#2A2010] rounded-lg p-3 border border-[#F39C12]/20">
              <p className="text-[#F39C12] text-xs mb-2">
                Ce produit n'est pas dans le catalogue. Sélectionnez le produit correspondant :
              </p>
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger data-testid="fallback-catalog-select">
                  <SelectValue placeholder="Choisir dans le catalogue..." />
                </SelectTrigger>
                <SelectContent>
                  {catalog.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} {p.reference && `(${p.reference})`} — {p.supplier_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}

      {/* Catalog selection */}
      {source === 'catalog' && (
        <div className="space-y-2">
          <Label>Produit du catalogue *</Label>
          <Select value={productId} onValueChange={setProductId}>
            <SelectTrigger data-testid="catalog-product-select">
              <SelectValue placeholder="Sélectionner un produit..." />
            </SelectTrigger>
            <SelectContent>
              {catalog.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} {p.reference && `(${p.reference})`} — {p.supplier_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Form fields */}
      <div className="space-y-2">
        <Label>Numéro de série <span className="text-[#52555A] text-xs">(optionnel)</span></Label>
        <Input
          value={serialNumber}
          onChange={(e) => setSerialNumber(e.target.value)}
          placeholder="Ex: FLT-2026-00142 (ajoutez-le plus tard si besoin)"
          data-testid="equip-serial-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Date d'achat</Label>
          <Input
            type="date"
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
            data-testid="equip-purchase-date"
          />
        </div>
        <div className="space-y-2">
          <Label>Date de livraison</Label>
          <Input
            type="date"
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
            data-testid="equip-delivery-date"
          />
        </div>
      </div>

      {purchaseAmount && (
        <div className="space-y-2">
          <Label>Montant TTC</Label>
          <Input
            type="number"
            value={purchaseAmount}
            onChange={(e) => setPurchaseAmount(e.target.value)}
            step="0.01"
            data-testid="equip-amount-input"
          />
        </div>
      )}

      {/* Selected product summary */}
      {productId && (
        <div className="bg-[#142915] rounded-lg p-3 border border-[#00E01A]/20">
          <p className="text-[#00E01A] text-xs font-semibold mb-1">Produit sélectionné</p>
          <p className="text-white text-sm">
            {catalog.find(c => c.id === productId)?.name || 'Produit'} 
            {selectedInvoiceProduct?.invoice_number && (
              <span className="text-[#8C8F94]"> — Réf. {selectedInvoiceProduct.invoice_number}</span>
            )}
          </p>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-[#2C3338]">
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button
          type="submit"
          disabled={saving || !productId}
          className="bg-[#00E01A] hover:bg-[#00B815] text-black"
          data-testid="equip-save-btn"
        >
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </form>
  );
};

const EditEquipmentDialog = ({ equipment, api, onUpdated, onClose }) => {
  const [serialNumber, setSerialNumber] = useState(equipment.serial_number || '');
  const [purchaseDate, setPurchaseDate] = useState(equipment.purchase_date ? equipment.purchase_date.slice(0, 10) : '');
  const [deliveryDate, setDeliveryDate] = useState(equipment.delivery_date ? equipment.delivery_date.slice(0, 10) : '');
  const [warrantyMonths, setWarrantyMonths] = useState(String(equipment.warranty_months || 24));
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/equipment/${equipment.id}`, {
        serial_number: serialNumber.trim() || null,
        purchase_date: purchaseDate || null,
        delivery_date: deliveryDate || null,
        warranty_months: parseInt(warrantyMonths) || 24,
      });
      toast.success('Équipement mis à jour');
      onUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-[#141617] rounded-lg p-3 border border-[#2C3338]">
        <p className="text-[#8C8F94] text-xs">Produit</p>
        <p className="text-white font-semibold">{equipment.product_name}</p>
        <p className="text-[#8C8F94] text-xs font-mono">{equipment.product_reference}</p>
      </div>

      <div className="space-y-2">
        <Label>Numéro de série</Label>
        <Input
          value={serialNumber}
          onChange={(e) => setSerialNumber(e.target.value)}
          placeholder="Ex: FLT-2026-00142"
          data-testid="edit-equip-serial"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Date d'achat</Label>
          <Input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} data-testid="edit-equip-purchase-date" />
        </div>
        <div className="space-y-2">
          <Label>Date de livraison</Label>
          <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} data-testid="edit-equip-delivery-date" />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Durée garantie (mois)</Label>
        <Input type="number" value={warrantyMonths} onChange={(e) => setWarrantyMonths(e.target.value)} min="0" data-testid="edit-equip-warranty" />
        {deliveryDate && (
          <p className="text-[#8C8F94] text-xs">
            Fin de garantie : {new Date(new Date(deliveryDate).getTime() + parseInt(warrantyMonths || 24) * 30 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR')}
          </p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-[#2C3338]">
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" disabled={saving} className="bg-[#00E01A] hover:bg-[#00B815] text-black" data-testid="edit-equip-save-btn">
          {saving ? 'Enregistrement...' : 'Mettre à jour'}
        </Button>
      </div>
    </form>
  );
};

const EditClientDialog = ({ client, franchises, api, onUpdated, onClose }) => {
  const [form, setForm] = useState({
    first_name: client.first_name || '',
    last_name: client.last_name || '',
    company: client.company || '',
    email: client.email || '',
    phone: client.phone || '',
    address: client.address || '',
    city: client.city || '',
    postal_code: client.postal_code || '',
    country: client.country || 'France',
    source: client.source || '',
    status: client.status || 'active',
    franchise_id: client.franchise_id || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}`, form);
      toast.success('Client mis à jour');
      onUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Prénom *</Label>
          <Input value={form.first_name} onChange={e => setForm(p => ({...p, first_name: e.target.value}))} required data-testid="edit-client-firstname" />
        </div>
        <div className="space-y-2">
          <Label>Nom *</Label>
          <Input value={form.last_name} onChange={e => setForm(p => ({...p, last_name: e.target.value}))} required data-testid="edit-client-lastname" />
        </div>
        <div className="space-y-2">
          <Label>Société</Label>
          <Input value={form.company} onChange={e => setForm(p => ({...p, company: e.target.value}))} data-testid="edit-client-company" />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} data-testid="edit-client-email" />
        </div>
        <div className="space-y-2">
          <Label>Téléphone</Label>
          <Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} data-testid="edit-client-phone" />
        </div>
        <div className="space-y-2">
          <Label>Statut</Label>
          <Select value={form.status} onValueChange={v => setForm(p => ({...p, status: v}))}>
            <SelectTrigger data-testid="edit-client-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Actif</SelectItem>
              <SelectItem value="vip">VIP</SelectItem>
              <SelectItem value="inactive">Inactif</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Adresse</Label>
          <Input value={form.address} onChange={e => setForm(p => ({...p, address: e.target.value}))} data-testid="edit-client-address" />
        </div>
        <div className="space-y-2">
          <Label>Code postal</Label>
          <Input value={form.postal_code} onChange={e => setForm(p => ({...p, postal_code: e.target.value}))} data-testid="edit-client-postal" />
        </div>
        <div className="space-y-2">
          <Label>Ville</Label>
          <Input value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))} data-testid="edit-client-city" />
        </div>
        <div className="space-y-2">
          <Label>Source</Label>
          <Input value={form.source} onChange={e => setForm(p => ({...p, source: e.target.value}))} placeholder="site web, salon, parrainage..." data-testid="edit-client-source" />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-[#2C3338]">
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" disabled={saving} className="bg-[#00E01A] hover:bg-[#00B815] text-black" data-testid="edit-client-save-btn">
          {saving ? 'Enregistrement...' : 'Mettre à jour'}
        </Button>
      </div>
    </form>
  );
};

const GmailMessageCard = ({ message }) => {
  const [collapsed, setCollapsed] = useState(true);
  const isSent = message.is_sent;
  const senderName = message.from_addr?.split('<')[0]?.trim() || message.from_addr || '?';
  const senderInitial = senderName?.[0]?.toUpperCase() || '?';

  const parseDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return dateStr; }
  };

  return (
    <div
      className="bg-[#1D2327] rounded-lg border border-[#2C3338] overflow-hidden"
      style={{ borderLeftWidth: 3, borderLeftColor: isSent ? '#00E01A' : '#3B82F6' }}
      data-testid={`gmail-msg-${message.id}`}
    >
      <div
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#141617] transition-colors"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
          style={{ backgroundColor: isSent ? '#00E01A' : '#3B82F6' }}
        >
          {senderInitial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white text-sm font-medium truncate">{senderName}</span>
            {isSent && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00E01A]/20 text-[#00E01A] font-semibold flex-shrink-0">Envoyé</span>
            )}
          </div>
          <p className="text-[#8C8F94] text-xs truncate">{message.subject || '(sans sujet)'}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[#52555A] text-xs">{parseDate(message.date)}</span>
          {collapsed ? <ChevronDown size={14} className="text-[#52555A]" /> : <ChevronUp size={14} className="text-[#52555A]" />}
        </div>
      </div>
      {!collapsed && (
        <div className="px-3 pb-3 ml-11 border-t border-[#2C3338] pt-2">
          <div className="text-[#8C8F94] text-xs mb-1">De : {message.from_addr}</div>
          <div className="text-[#8C8F94] text-xs mb-2">À : {message.to_addr}</div>
          <pre className="text-[#ccc] text-sm whitespace-pre-wrap leading-relaxed max-h-[300px] overflow-y-auto" style={{ fontFamily: "'Barlow', sans-serif" }}>
            {message.body}
          </pre>
        </div>
      )}
    </div>
  );
};

const ClientPassport = () => {
  const { api } = useAuth();
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [equipDialogOpen, setEquipDialogOpen] = useState(false);
  const [editEquipment, setEditEquipment] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [franchises, setFranchises] = useState([]);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailMessages, setGmailMessages] = useState([]);
  const [gmailSyncing, setGmailSyncing] = useState(false);
  const [gmailLoading, setGmailLoading] = useState(true);
  const [locationStats, setLocationStats] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);

  const handleDeleteEquipment = async (eq) => {
    try {
      await api.delete(`/equipment/${eq.id}`);
      toast.success('Équipement supprimé');
      setDeleteConfirm(null);
      fetchPassport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const fetchPassport = async () => {
    try {
      const [passportRes, invoicesRes, franchisesRes] = await Promise.all([
        api.get(`/clients/${clientId}/passport`),
        api.get(`/axonaut/invoices?client_id=${clientId}`).catch(() => ({ data: [] })),
        api.get('/franchises').catch(() => ({ data: [] }))
      ]);
      setData(passportRes.data);
      setInvoices(invoicesRes.data || []);
      setFranchises(franchisesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch passport:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchGmailData = async () => {
    setGmailLoading(true);
    try {
      const [statusRes, messagesRes] = await Promise.all([
        api.get('/gmail/status'),
        api.get(`/gmail/messages/${clientId}`).catch(() => ({ data: [] })),
      ]);
      setGmailConnected(statusRes.data.connected);
      setGmailMessages(messagesRes.data || []);
    } catch {
      setGmailConnected(false);
    } finally {
      setGmailLoading(false);
    }
  };

  const fetchLocationStats = async () => {
    setLocationLoading(true);
    try {
      const res = await api.get(`/clients/${clientId}/location-stats`);
      setLocationStats(res.data);
    } catch {
      setLocationStats(null);
    } finally {
      setLocationLoading(false);
    }
  };

  const handleGmailConnect = async () => {
    try {
      const res = await api.get('/oauth/gmail/login');
      window.open(res.data.auth_url, '_blank', 'width=600,height=700');
      toast.success('Fenêtre de connexion Gmail ouverte. Autorisez l\'accès puis revenez ici.');
      // Poll for connection
      const interval = setInterval(async () => {
        try {
          const statusRes = await api.get('/gmail/status');
          if (statusRes.data.connected) {
            clearInterval(interval);
            setGmailConnected(true);
            toast.success('Gmail connecté !');
          }
        } catch {}
      }, 3000);
      setTimeout(() => clearInterval(interval), 120000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur connexion Gmail');
    }
  };

  const handleGmailSync = async () => {
    setGmailSyncing(true);
    try {
      const res = await api.post(`/gmail/sync/${clientId}`);
      toast.success(`${res.data.synced} nouveaux emails synchronisés (${res.data.total} total)`);
      fetchGmailData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur synchronisation Gmail');
    } finally {
      setGmailSyncing(false);
    }
  };

  useEffect(() => {
    fetchPassport();
    fetchGmailData();
    fetchLocationStats();
  }, [api, clientId]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-[#2C3338] rounded mb-4"></div>
          <div className="bg-[#1D2327] rounded-xl p-6">
            <div className="h-6 w-32 bg-[#2C3338] rounded mb-4"></div>
            <div className="h-4 w-64 bg-[#2C3338] rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center">
        <p className="text-[#8C8F94]">Client non trouvé</p>
      </div>
    );
  }

  const { client, equipment, sav_tickets, timeline, stats } = data;

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/clients"
          className="p-2 rounded-lg bg-[#1D2327] border border-[#2C3338] text-[#8C8F94] hover:text-white hover:border-[#00E01A]/30 transition-colors"
          data-testid="back-to-clients-btn"
        >
          <ArrowLeft size={20} />
        </Link>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">
              {client.full_name}
            </h1>
            {client.status === 'vip' && <Star size={20} className="text-[#F39C12]" />}
          </div>
          <p className="text-[#8C8F94]">Passeport client</p>
        </div>
        <button
          onClick={() => setEditClientOpen(true)}
          className="ml-auto flex items-center gap-2 bg-[#1D2327] hover:bg-[#2C3338] text-white font-medium px-4 py-2 rounded-full border border-[#2C3338] transition-all text-sm"
          data-testid="edit-client-btn"
        >
          <Edit size={16} /> Modifier
        </button>
      </div>

      {/* Edit Client Dialog */}
      <Dialog open={editClientOpen} onOpenChange={setEditClientOpen}>
        <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold font-['Outfit']">Modifier le client</DialogTitle>
          </DialogHeader>
          {data?.client && (
            <EditClientDialog
              client={data.client}
              franchises={franchises}
              api={api}
              onUpdated={() => { setEditClientOpen(false); fetchPassport(); }}
              onClose={() => setEditClientOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Client Info & Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Contact Info */}
        <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5">
          <h3 className="text-white font-semibold mb-4">Informations</h3>
          <div className="space-y-3">
            {client.company && (
              <div className="flex items-center gap-3 text-[#8C8F94]">
                <Building2 size={16} />
                <span>{client.company}</span>
              </div>
            )}
            {client.email && (
              <div className="flex items-center gap-3 text-[#8C8F94]">
                <Mail size={16} />
                <a href={`mailto:${client.email}`} className="hover:text-[#00E01A] transition-colors">
                  {client.email}
                </a>
              </div>
            )}
            {client.phone && (
              <div className="flex items-center gap-3 text-[#8C8F94]">
                <Phone size={16} />
                <a href={`tel:${client.phone}`} className="hover:text-[#00E01A] transition-colors">
                  {client.phone}
                </a>
              </div>
            )}
            {client.city && (
              <div className="flex items-center gap-3 text-[#8C8F94]">
                <MapPin size={16} />
                <span>{client.address && `${client.address}, `}{client.postal_code} {client.city}</span>
              </div>
            )}
          </div>
          
          <div className="mt-4 pt-4 border-t border-[#2C3338]">
            <p className="text-[#8C8F94] text-sm">Franchise</p>
            <p className="text-white">{client.franchise_name}</p>
          </div>

          <button
            className="w-full mt-4 flex items-center justify-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold py-2.5 px-4 rounded-full transition-all"
            data-testid="send-email-btn"
          >
            <Send size={16} />
            Envoyer un email
          </button>
        </div>

        {/* Stats */}
        <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5">
          <h3 className="text-white font-semibold mb-4">Statistiques</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-[#141617] rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-white">{stats.equipment_count}</p>
              <p className="text-[#8C8F94] text-xs">Équipements</p>
            </div>
            <div className="bg-[#141617] rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-[#00E01A]">
                {client.total_ca ? `${(client.total_ca / 1000).toFixed(0)}k€` : '0€'}
              </p>
              <p className="text-[#8C8F94] text-xs">CA total</p>
            </div>
            <div className="bg-[#141617] rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-white">{stats.open_sav}</p>
              <p className="text-[#8C8F94] text-xs">SAV ouverts</p>
            </div>
            <div className="bg-[#141617] rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-white">{stats.closed_sav}</p>
              <p className="text-[#8C8F94] text-xs">SAV clôturés</p>
            </div>
          </div>
          
          {stats.total_maintenance_cost > 0 && (
            <div className="mt-3 p-3 bg-[#141617] rounded-lg">
              <p className="text-[#8C8F94] text-xs">Coût maintenance total</p>
              <p className="text-white font-bold">{stats.total_maintenance_cost.toLocaleString()}€</p>
            </div>
          )}
          
          {stats.warranties_expiring_soon > 0 && (
            <div className="mt-3 p-3 bg-[#2A2010] rounded-lg flex items-center gap-2">
              <AlertTriangle size={16} className="text-[#F39C12]" />
              <span className="text-[#F39C12] text-sm">
                {stats.warranties_expiring_soon} garantie(s) expire(nt) bientôt
              </span>
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5">
          <h3 className="text-white font-semibold mb-4">Historique récent</h3>
          {timeline.length === 0 ? (
            <p className="text-[#8C8F94] text-sm">Aucun événement</p>
          ) : (
            <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
              {timeline.slice(0, 10).map((item, idx) => (
                <TimelineItem key={idx} item={item} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Location (Rental) Section */}
      {locationStats && locationStats.stats?.total_sessions > 0 && (
        <div data-testid="location-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white font-['Outfit'] flex items-center gap-2">
              <Calendar size={20} className="text-[#3B82F6]" />
              Location ({locationStats.stats.total_sessions} sessions)
            </h2>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              locationStats.stats.segment === 'VIP' ? 'bg-[#F39C12]/15 text-[#F39C12]' :
              locationStats.stats.segment === 'regulier' ? 'bg-[#00E01A]/15 text-[#00E01A]' :
              locationStats.stats.segment === 'decouverte' ? 'bg-[#3B82F6]/15 text-[#3B82F6]' :
              'bg-[#2C3338] text-[#8C8F94]'
            }`} data-testid="client-segment-badge">
              {locationStats.stats.segment === 'VIP' ? 'Client VIP' :
               locationStats.stats.segment === 'regulier' ? 'Client Régulier' :
               locationStats.stats.segment === 'decouverte' ? 'Découverte' : 'Nouveau'}
            </span>
          </div>

          {/* Location Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4 text-center">
              <p className="text-2xl font-bold text-white">{locationStats.stats.total_sessions}</p>
              <p className="text-[#8C8F94] text-xs">Sessions</p>
            </div>
            <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4 text-center">
              <p className="text-2xl font-bold text-[#00E01A]">{locationStats.stats.total_ca?.toLocaleString('fr-FR')}€</p>
              <p className="text-[#8C8F94] text-xs">CA Location</p>
            </div>
            <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4 text-center">
              <p className="text-sm font-bold text-white">
                {locationStats.stats.last_booking ? new Date(locationStats.stats.last_booking).toLocaleDateString('fr-FR') : '-'}
              </p>
              <p className="text-[#8C8F94] text-xs">Dernière session</p>
            </div>
            <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4 text-center">
              <p className="text-lg font-bold text-white">{locationStats.stats.sites?.length || 0}</p>
              <p className="text-[#8C8F94] text-xs">Site(s)</p>
            </div>
          </div>

          {/* Sites badges */}
          {locationStats.stats.sites?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {locationStats.stats.sites.map(s => (
                <span key={s.id} className="px-3 py-1 rounded-full text-xs bg-[#1D2327] border border-[#2C3338] text-white">
                  {s.name}
                </span>
              ))}
            </div>
          )}

          {/* Recent bookings */}
          {locationStats.bookings?.length > 0 && (
            <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#2C3338]">
                      <th className="text-left p-3 text-[#8C8F94] text-xs font-semibold uppercase">Date</th>
                      <th className="text-left p-3 text-[#8C8F94] text-xs font-semibold uppercase">Activité</th>
                      <th className="text-left p-3 text-[#8C8F94] text-xs font-semibold uppercase">Site</th>
                      <th className="text-right p-3 text-[#8C8F94] text-xs font-semibold uppercase">Montant</th>
                      <th className="text-left p-3 text-[#8C8F94] text-xs font-semibold uppercase">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locationStats.bookings.slice(0, 10).map((b) => (
                      <tr key={b.id || b.booking_reference} className="border-b border-[#2C3338] last:border-0 hover:bg-[#141617]/50">
                        <td className="p-3 text-white text-sm">{b.date ? new Date(b.date).toLocaleDateString('fr-FR') : '-'}</td>
                        <td className="p-3 text-[#8C8F94] text-sm">{b.activity_type || '-'}</td>
                        <td className="p-3 text-[#8C8F94] text-sm">{b.site_id || '-'}</td>
                        <td className="p-3 text-right text-white font-semibold text-sm">{(b.amount || 0).toLocaleString('fr-FR')}€</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            b.status === 'confirmé' ? 'bg-[#142915] text-[#00E01A]' :
                            b.status === 'annulé' ? 'bg-[#2A1515] text-[#EF4444]' :
                            'bg-[#2A2010] text-[#F59E0B]'
                          }`}>{b.status || '-'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Equipment */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white font-['Outfit']">
            Équipements ({equipment.length})
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setEquipDialogOpen(true)}
              className="flex items-center gap-1.5 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-3 py-1.5 rounded-full text-sm transition-all shadow-[0_0_10px_rgba(0,224,26,0.2)]"
              data-testid="register-equipment-btn"
            >
              <Plus size={14} />
              Enregistrer
            </button>
            <Link
              to={`/inventory?client_id=${clientId}`}
              className="text-sm text-[#8C8F94] hover:text-[#00E01A] flex items-center gap-1 transition-colors"
              data-testid="view-all-equipment-link"
            >
              Voir tout <ChevronRight size={16} />
            </Link>
          </div>
        </div>

        {/* Register Equipment Dialog */}
        <Dialog open={equipDialogOpen} onOpenChange={setEquipDialogOpen}>
          <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Outfit']">
                Enregistrer un équipement
              </DialogTitle>
            </DialogHeader>
            <RegisterEquipmentDialog
              clientId={clientId}
              invoices={invoices}
              api={api}
              onCreated={() => {
                setEquipDialogOpen(false);
                fetchPassport();
              }}
              onClose={() => setEquipDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
        
        {equipment.length === 0 ? (
          <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-8 text-center">
            <Package size={48} className="mx-auto text-[#8C8F94] mb-4" />
            <p className="text-[#8C8F94]">Aucun équipement enregistré</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {equipment.map((eq) => (
              <EquipmentCard
                key={eq.id}
                equipment={eq}
                onEdit={(e) => setEditEquipment(e)}
                onDelete={(e) => setDeleteConfirm(e)}
              />
            ))}
          </div>
        )}

        {/* Edit Equipment Dialog */}
        <Dialog open={!!editEquipment} onOpenChange={(open) => !open && setEditEquipment(null)}>
          <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Outfit']">Modifier l'équipement</DialogTitle>
            </DialogHeader>
            {editEquipment && (
              <EditEquipmentDialog
                equipment={editEquipment}
                api={api}
                onUpdated={() => { setEditEquipment(null); fetchPassport(); }}
                onClose={() => setEditEquipment(null)}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
          <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Outfit']">Supprimer l'équipement</DialogTitle>
            </DialogHeader>
            {deleteConfirm && (
              <div className="space-y-4">
                <p className="text-[#8C8F94]">
                  Voulez-vous vraiment supprimer <span className="text-white font-semibold">{deleteConfirm.product_name}</span>
                  {deleteConfirm.serial_number && <> (N° {deleteConfirm.serial_number})</>} ?
                </p>
                <div className="flex justify-end gap-3 pt-4 border-t border-[#2C3338]">
                  <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Annuler</Button>
                  <Button
                    onClick={() => handleDeleteEquipment(deleteConfirm)}
                    className="bg-[#E74C3C] hover:bg-[#C0392B] text-white"
                    data-testid="confirm-delete-equip-btn"
                  >
                    <Trash2 size={14} className="mr-2" /> Supprimer
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* SAV Tickets */}
      {sav_tickets.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white font-['Outfit']">
              Tickets SAV ({sav_tickets.length})
            </h2>
            <Link
              to={`/sav?client_id=${clientId}`}
              className="text-sm text-[#8C8F94] hover:text-[#00E01A] flex items-center gap-1 transition-colors"
              data-testid="view-all-sav-link"
            >
              Voir tout <ChevronRight size={16} />
            </Link>
          </div>
          
          <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#2C3338]">
                    <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase">N° Ticket</th>
                    <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase">Produit</th>
                    <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase">Statut</th>
                    <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {sav_tickets.slice(0, 5).map((ticket) => (
                    <tr key={ticket.id} className="border-b border-[#2C3338] last:border-0 hover:bg-[#141617]/50">
                      <td className="p-4">
                        <Link 
                          to={`/sav/${ticket.id}`}
                          className="text-[#00E01A] font-mono text-sm hover:underline"
                        >
                          {ticket.ticket_number}
                        </Link>
                      </td>
                      <td className="p-4 text-white">{ticket.description?.slice(0, 50)}...</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          ticket.status === 'closed' ? 'bg-[#142915] text-[#00E01A]' :
                          ticket.status === 'open' ? 'bg-[#2A1515] text-[#E74C3C]' :
                          'bg-[#2A2010] text-[#F39C12]'
                        }`}>
                          {ticket.status === 'open' ? 'Ouvert' :
                           ticket.status === 'closed' ? 'Clôturé' :
                           ticket.status === 'diagnosed' ? 'Diagnostiqué' :
                           ticket.status === 'waiting_parts' ? 'Attente pièces' :
                           ticket.status === 'in_repair' ? 'En réparation' :
                           'Prêt'}
                        </span>
                      </td>
                      <td className="p-4 text-[#8C8F94] text-sm">
                        {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Gmail Emails */}
      <div data-testid="gmail-emails-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white font-['Outfit'] flex items-center gap-2">
            <Mail size={20} className="text-[#00E01A]" />
            Emails Gmail ({gmailMessages.length})
          </h2>
          <div className="flex items-center gap-2">
            {gmailConnected ? (
              <>
                <span className="flex items-center gap-1.5 text-xs text-[#00E01A] bg-[#142915] px-2 py-1 rounded-full border border-[#00E01A]/20" data-testid="gmail-connected-badge">
                  <Check size={10} /> Connecté
                </span>
                <button
                  onClick={handleGmailSync}
                  disabled={gmailSyncing || !client.email}
                  className="flex items-center gap-1.5 text-xs bg-[#1D2327] hover:bg-[#2C3338] text-white border border-[#2C3338] px-3 py-1.5 rounded-full transition-all disabled:opacity-50"
                  data-testid="gmail-sync-btn"
                >
                  <RefreshCw size={12} className={gmailSyncing ? 'animate-spin' : ''} />
                  {gmailSyncing ? 'Synchronisation...' : 'Synchroniser'}
                </button>
              </>
            ) : (
              <button
                onClick={handleGmailConnect}
                className="flex items-center gap-2 bg-[#1D2327] hover:bg-[#2C3338] text-white border border-[#2C3338] px-4 py-2 rounded-full transition-all text-sm"
                data-testid="gmail-connect-btn"
              >
                <Mail size={14} /> Connecter Gmail
              </button>
            )}
          </div>
        </div>

        {!client.email ? (
          <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-6 text-center">
            <Mail size={36} className="mx-auto text-[#52555A] mb-2" />
            <p className="text-[#8C8F94] text-sm">Ce client n'a pas d'adresse email. Ajoutez un email pour synchroniser les échanges Gmail.</p>
          </div>
        ) : !gmailConnected ? (
          <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-6 text-center">
            <Mail size={36} className="mx-auto text-[#52555A] mb-2" />
            <p className="text-[#8C8F94] text-sm">Connectez votre compte Gmail pour voir les emails échangés avec <span className="text-white">{client.email}</span></p>
          </div>
        ) : gmailMessages.length === 0 ? (
          <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-6 text-center">
            <Mail size={36} className="mx-auto text-[#52555A] mb-2" />
            <p className="text-[#8C8F94] text-sm">Aucun email synchronisé. Cliquez sur "Synchroniser" pour récupérer les emails avec <span className="text-white">{client.email}</span></p>
          </div>
        ) : (
          <div className="space-y-2">
            {gmailMessages.map(msg => (
              <GmailMessageCard key={msg.id} message={msg} />
            ))}
          </div>
        )}
      </div>

      {/* Axonaut Invoices */}
      {invoices.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white font-['Outfit']">
              Factures Axonaut ({invoices.length})
            </h2>
            <span className="text-xs text-[#8C8F94] bg-[#2C3338] px-2 py-1 rounded">
              {invoices.reduce((sum, i) => sum + (i.total_ttc || 0), 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€ TTC
            </span>
          </div>
          
          <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#2C3338]">
                    <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase">N° Facture</th>
                    <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase">Date</th>
                    <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase hidden md:table-cell">Produits</th>
                    <th className="text-right p-4 text-[#8C8F94] text-xs font-semibold uppercase">Montant TTC</th>
                    <th className="text-left p-4 text-[#8C8F94] text-xs font-semibold uppercase">Statut</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.axonaut_id || inv.id} className="border-b border-[#2C3338] last:border-0 hover:bg-[#141617]/50">
                      <td className="p-4">
                        <span className="text-white font-mono text-sm">{inv.number}</span>
                      </td>
                      <td className="p-4 text-[#8C8F94] text-sm">
                        {inv.date ? new Date(inv.date).toLocaleDateString('fr-FR') : '-'}
                      </td>
                      <td className="p-4 hidden md:table-cell">
                        <div className="space-y-0.5 max-w-xs">
                          {(inv.invoice_lines || []).slice(0, 2).map((line, idx) => (
                            <p key={idx} className="text-[#8C8F94] text-xs truncate">
                              {line.quantity > 1 && `${line.quantity}x `}{line.product_name}
                            </p>
                          ))}
                          {(inv.invoice_lines || []).length > 2 && (
                            <p className="text-[#52555A] text-xs">
                              + {inv.invoice_lines.length - 2} autre(s)
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <span className="text-white font-semibold">
                          {(inv.total_ttc || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          inv.payment_status === 'paid' ? 'bg-[#142915] text-[#00E01A]' :
                          inv.payment_status === 'partial' ? 'bg-[#2A2010] text-[#F39C12]' :
                          'bg-[#2A1515] text-[#E74C3C]'
                        }`}>
                          {inv.payment_status === 'paid' ? 'Payée' :
                           inv.payment_status === 'partial' ? 'Partiel' : 'Impayée'}
                        </span>
                      </td>
                      <td className="p-4">
                        {inv.public_path && (
                          <a
                            href={inv.public_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#8C8F94] hover:text-[#00E01A] transition-colors"
                            title="Voir PDF"
                            data-testid={`invoice-pdf-${inv.axonaut_id}`}
                          >
                            <ExternalLink size={16} />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPassport;
